import React, { useEffect, useState } from "react";
import { Button, Card, CardHeader, Form } from "reactstrap";
import { useDispatch, useSelector } from "react-redux";
import { Table, TableRow } from "../../../components/Table";
import {
  blockInfoGet,
  useBlockInfoState,
} from "../../../redux/slices/blockInfoSlice";
import { TRA_FILTER } from "../../../constant";
import { useRole } from "../../../redux/slices/authSlice";
import BlockInfoModal from "../../../components/Custom/Modals/BlockInfo/BlockInfoModal";
import { FaRegEye } from "react-icons/fa";
import BlockInfoShowModal from "./BlockInfoShowModal";
import ControlledInput from "../../../components/Custom/Forms/Controller/ControlledInput";
import { useForm } from "react-hook-form";
import BlockInfoShowContent from "./BlockInfoShowContent";
import ControlledDropDown from "../../../components/Custom/Forms/Controller/ControlledDropDown";
import { capitalCaseLabel } from "../../../helperFunctions";

export const prepareFieldName = () => {
  const options = [
    { key: "browser_id" },
    { key: "customer_id" },
    { key: "customer_ip" },
    { key: "customer_email" },
    { key: "customer_mobile" },
    { key: "cust_state", value: "Customer State" },
    { key: "cust_city", value: "Customer City" },
    { key: "payment_data" },
  ]?.map?.((item) => {
    const clientDataOption = {
      value: item?.key,
      label: item?.value || capitalCaseLabel?.(item?.key),
    };
    return clientDataOption;
  });
  return options;
};

const BlockInfo = () => {
  const dispatch = useDispatch();
  const [filter, setFilter] = useState(TRA_FILTER);
  const blockInfo = useBlockInfoState();
  const blockInfoState = Object.entries?.(blockInfo?.data || {})?.map?.(
    ([k, v]) => {
      const { merchant_details, ...rest } = v;
      return {
        merchantId: k,
        merchantName: v?.merchant_details?.admin_merchant_name,
        values: rest,
      };
    }
  );
  const role = useRole("Block Info");
  const { blockInfoIsLoading } = useSelector((state) => state?.blockInfo);
  const [filterColumns, setFilterColumns] = useState([]);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isShowModalOpen, setIsShowModalOpen] = useState(false);
  const [showInlineModalData, setShowInlineModalData] = useState(null);

  const initialValues = {
    filter: prepareFieldName()?.[0]?.value,
    blockData: "",
    columnName: "",
  };
  const { control, getValues, handleSubmit, reset } = useForm({
    defaultValues: initialValues,
  });

  const onSubmit = async () => {
    const values = getValues();
    setFilter({
      ...filter,
      ...values,
      ...TRA_FILTER,
      isFilter: true,
    });
  };

  const columns = [
    {
      title: "Id",
      name: "merchantId",
    },
    {
      title: "Merchant",
      name: "merchantName",
    },
    {
      title: "ACTION",
      name: "action",
      Cell: (data) => {
        return (
          <>
            <div className="d-flex flex-row gap-50">
              {role?.includes("blockInfo-list") ? (
                <Button
                  color="primary"
                  className="btn-smallsize"
                  onClick={() => setIsShowModalOpen(data)}
                  title="Show Info"
                >
                  <FaRegEye />
                </Button>
              ) : null}
            </div>
          </>
        );
      },
    },
  ];

  useEffect(() => {
    const newColumns = columns?.map((item) => item?.name);
    setFilterColumns(newColumns);
  }, []);

  const BlockInfoGet = async () => {
    try {
      const { isFilter, filter: dropdown, blockData, page_no, limit } = filter;
      const payload = isFilter
        ? { filter_data: { columnName: dropdown, blockData }, page_no, limit }
        : {
          page_no,
          limit,
        };
      const res = await dispatch(blockInfoGet(payload)).unwrap();
      if (isFilter) {
        setShowInlineModalData(
          Object.entries?.(res?.data || {})?.map?.(([key, data]) => {
            const { merchant_details, ...rest } = data;

            return {
              merchantId: merchant_details?.merchant_id || key,
              merchantName: merchant_details?.admin_merchant_name,
              values: rest,
            };
          })
        );
      }
    } catch (err) {
      if (filter?.isFilter) {
        setShowInlineModalData([]);
      }
    }
  };
  useEffect(() => {
    if (role?.includes?.("blockInfo-list")) {
      BlockInfoGet();
    }
  }, [filter]);

  if (!role.includes?.("blockInfo-list")) {
    return;
  }

  return (
    <>
      <Card>
        <CardHeader className="flex-column align-items-start">
          <div className="d-flex w-100 justify-content-between">
            <div>
              <h4 className="main-title">
                <b>Block Info</b>
              </h4>
              <Form
                className="d-flex my-xl-auto right-content align-items-end button-space flex-wrap py-1 "
                onSubmit={handleSubmit(onSubmit)}
              >
                <ControlledDropDown
                  name="filter"
                  label="Filter"
                  isDefaultOption={false}
                  options={prepareFieldName()}
                  control={control}
                />
                <ControlledInput
                  name="blockData"
                  placeholder="Enter Search Value"
                  label="Search"
                  control={control}
                />
                <Button type="submit" color="primary">
                  Apply
                </Button>
                <Button
                  type="button"
                  color="danger "
                  onClick={() => {
                    reset();
                    setFilter({ ...TRA_FILTER });
                    setShowInlineModalData(false);
                  }}
                >
                  Clear
                </Button>
              </Form>
            </div>
            <div className="d-flex justify-content-end button-space">
              {role?.includes?.("blockInfo-add") ? (
                <Button
                  color="primary"
                  title="Block Info"
                  onClick={() => setIsModalOpen("block-city-state")}
                >
                  Block City & State
                </Button>
              ) : null}
              {role?.includes?.("blockInfo-add") ? (
                <Button
                  color="primary"
                  title="Block Info"
                  onClick={() => setIsModalOpen(true)}
                >
                  Add Block Info
                </Button>
              ) : null}
            </div>
          </div>
        </CardHeader>
        {filter?.isFilter ? (
          <BlockInfoShowContent
            data={showInlineModalData}
            loading={blockInfoIsLoading}
            onGet={() => BlockInfoGet()}
            onClose={() => {
              setShowInlineModalData(false);
            }}
          />
        ) : (
          <Table
            columns={columns}
            isLoading={blockInfoIsLoading}
            data={blockInfoState || []}
            isData={!!blockInfoState?.length}
            filterColumns={filterColumns}
            count={blockInfo?.total_item || 0}
            pagination={filter}
            isExpandable={false}
            handlePaginationChange={(pagination) => {
              setFilter({
                ...filter,
                ...pagination,
              });
            }}
            onColumnsChange={(columns) => {
              setFilterColumns(columns);
            }}
          >
            {blockInfoState?.map?.((item) => {
              return (
                <TableRow
                  columns={columns}
                  item={item}
                  filterColumns={filterColumns}
                  isExpandable={false}
                />
              );
            })}
          </Table>
        )}
      </Card>
      <BlockInfoModal
        isModalOpen={isModalOpen}
        onGet={() => BlockInfoGet()}
        onCloseModal={() => {
          setIsModalOpen(false);
        }}
      />
      <BlockInfoShowModal
        data={isShowModalOpen}
        onGet={() => BlockInfoGet()}
        onClose={() => {
          setIsShowModalOpen(false);
        }}
      />
    </>
  );
};

export default BlockInfo;
